import React, { useCallback } from "react";
import axios from "axios";
import { Table, Button, OverlayTrigger, Tooltip, Toast, ToastContainer } from "react-bootstrap";
import { useEffect, useState } from "react";
import GLOBALVAR from "../constants";
import { format, parseISO } from 'date-fns';
import LoadingSpinner from "../components/misc/LoadingSpinner";
import jsPDF from "jspdf";
import 'jspdf-autotable';
import './css/ReportsStyles.scss';
import ToPeso from "../components/misc/ToPeso";
import logo from "../logo.png";
import calculateDueDates from "../components/misc/calculateDueDates";
import calculatePayments from "../components/misc/calculatePayments";
import { calculateAmountDue } from '../utils/loanCalculations';

// Function to fetch all penalties at once with optional filters
const fetchAllPenalties = async (endDate) => {
    try {
        const queryParams = {};
        
        // Add endDate filter if provided
        if (endDate) {
            queryParams.endDate = endDate;
        }
        
        // Set a large limit to get all penalties
        queryParams.limit = 1000;
        
        const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/penalties`, {
            params: queryParams
        });
        
        // Transform the response into a map of loanID -> penalties
        const penaltiesByLoanId = {};
        
        if (response.data && response.data.data) {
            // Group penalties by loanID first
            const groupedByLoanId = {};
            
            response.data.data.forEach(penalty => {
                const loanId = penalty.loanID;
                
                if (!groupedByLoanId[loanId]) {
                    groupedByLoanId[loanId] = [];
                }
                
                groupedByLoanId[loanId].push(penalty);
            });
            
            // For each loan, group penalties by lastPaymentDate and get the latest one
            Object.keys(groupedByLoanId).forEach(loanId => {
                const loanPenalties = groupedByLoanId[loanId];
                
                // Step 1: Filter unpaid penalties
                const unpaidPenalties = loanPenalties.filter(penalty => !penalty.isPaid);
                
                // Step 2: Group unpaid penalties by lastPaymentDate
                const groupedByDate = {};
                unpaidPenalties.forEach(penalty => {
                    const paymentDate = penalty.lastPaymentDate || 'unknown';
                    if (!groupedByDate[paymentDate]) {
                        groupedByDate[paymentDate] = [];
                    }
                    groupedByDate[paymentDate].push(penalty);
                });
                
                // Step 3: Get only the latest record for each lastPaymentDate group
                const latestRecordsPerDate = Object.values(groupedByDate).map(group => {
                    // Sort by createdAt or calculationDate (newest first) and take the first item
                    return group.sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.calculationDate);
                        const dateB = new Date(b.createdAt || b.calculationDate);
                        return dateB - dateA;
                    })[0];
                });
                
                // Store the filtered penalties in the map
                penaltiesByLoanId[loanId] = latestRecordsPerDate;
            });
        }
        
        return penaltiesByLoanId;
    } catch (error) {
        console.error('Error fetching all penalties:', error);
        return {};
    }
};

// Function to get penalty amount for a specific loan from the penalties map
const getPenaltyAmountFromAPI = (penaltiesMap, loanId) => {
    // Get penalties for the specific loan
    const loanPenalties = penaltiesMap[loanId] || [];
    
    // Calculate the total unpaid penalty amount
    // Now we're using the already filtered penalties (only the latest per payment date)
    const totalPenaltyAmount = loanPenalties.reduce((sum, penalty) => {
        const penaltyAmount = parseFloat(penalty.penaltyAmount) || 0;
        const paidAmount = parseFloat(penalty.paidAmount) || 0;
        return sum + (penaltyAmount - paidAmount);
    }, 0);
    
    return totalPenaltyAmount;
};

function ReportLoanListingByLoanStatus() {
    const [paymentHeaders, setPaymentHeaders] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [loanCurrent, setLoanCurrent] = useState(null);
    const [loanPastdue, setLoanPastdue] = useState(null);
    const [loanCurrentTotals, setLoanCurrentTotals] = useState(null);
    const [loanPastdueTotals, setLoanPastdueTotals] = useState(null);
    
    // Add state for toast notification
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastBorrower, setToastBorrower] = useState('');

    const copyToClipboard = (fullName, amountDue, penalty, totalAmountDue) => {
        // Add transfer fee
        const transferFee = 25.00;
        
        // Calculate new total including transfer fee
        const newTotalDue = parseFloat(totalAmountDue) + transferFee;
        
        // Format the text to be copied, now including transfer fee
        const textToCopy = `${fullName}\nAmount Due: ₱${parseFloat(amountDue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\nPenalty: ₱${parseFloat(penalty).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\nTransfer Fee: ₱${transferFee.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\nTotal: ₱${newTotalDue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                // Show toast notification
                setToastBorrower(fullName);
                setToastMessage('Payment details copied to clipboard');
                setShowToast(true);
            })
            .catch(err => {
                console.error('Could not copy text: ', err);
                // Show error toast
                setToastBorrower(fullName);
                setToastMessage('Failed to copy to clipboard');
                setShowToast(true);
            });
    };

    // Use useCallback to memoize the fetchLoans function
    const fetchLoans = useCallback(async (endDate) => {
        setShowSpinner(true);

        try {
            // First fetch all penalties
            const allPenalties = await fetchAllPenalties(endDate);

            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/loanstatus`, {
                params: {
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });

            // Process current loans
            const loansDataCurrent = {};
            for (const item of response.data.current.loans) {
                const fullName = `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`;

                try {
                    const dates = calculateDueDates(
                        parseISO(item.dateGranted), 
                        item.loanInterval, 
                        item.loanTerm, 
                        item.amountGranted, 
                        item.interestRate, 
                        item.advanceInterest
                    );
                    const paymentsArr = calculatePayments(
                        parseISO(item.dateGranted), 
                        item.loanInterval, 
                        item.loanTerm, 
                        item.amountGranted, 
                        item.interestRate, 
                        item.payments, 
                        item.advanceInterest
                    );
                    const textdates = `${format(parseISO(item.dateGranted), 'MM/dd/yyyy')} - ${dates[dates.length - 1].date}`;
                    
                    const baseAmountDue = calculateAmountDue(dates, item.payments, endDate ? parseISO(endDate) : new Date());
                    // Get penalty from the penalties map
                    const penaltyAmount = getPenaltyAmountFromAPI(allPenalties, item.id);
                    const totalDue = baseAmountDue + penaltyAmount;

                    const fullNameWithLink = (
                        <div className="d-flex align-items-center">
                            <a href={`/applicant/${item.applicant.id}`} className="me-2">
                                {fullName}
                            </a>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Copy payment details</Tooltip>}
                            >
                                <button 
                                    className="btn btn-sm btn-outline-secondary copy-btn"
                                    onClick={() => copyToClipboard(fullName, baseAmountDue, penaltyAmount, totalDue)}
                                >
                                    <i className="fas fa-copy"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    );

                    const loansnum = item.id.toString().padStart(6, '0');
                    const loansNumWithLink = (
                        <a href={`/loans/${item.id}`}>
                            {loansnum}
                        </a>
                    );

                    const loanDetails = {
                        items: [
                            { key: 'fullName', value: fullNameWithLink },
                            { key: 'loanProcessingID', value: loansNumWithLink },
                            { key: 'dateGrantedDue', value: textdates },
                            { key: 'amountGranted', value: <ToPeso amount={item.amountGranted} /> },
                            { key: 'interestAmount', value: <ToPeso amount={((item.amountGranted * (item.interestRate/100)) * item.loanTerm)} /> },
                            { key: 'totalBalance', value: <ToPeso amount={paymentsArr.remainingBalance} /> },
                            { key: 'interestRate', value: `${item.interestRate}%` },
                            { key: 'term', value: item.loanTerm },
                            { key: 'amountDue', value: <ToPeso amount={baseAmountDue} /> },
                            { key: 'penalty', value: <ToPeso amount={penaltyAmount} /> },
                            { key: 'totalAmountDue', value: <ToPeso amount={totalDue} /> }
                        ]
                    };

                    loansDataCurrent[item.id] = loanDetails;
                } catch (error) {
                    console.error(`Error processing loan ${item.id}:`, error);
                    const fullNameWithLink = (
                        <div className="d-flex align-items-center">
                            <a href={`/applicant/${item.applicant.id}`} className="me-2">
                                {fullName}
                            </a>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Copy payment details</Tooltip>}
                            >
                                <button 
                                    className="btn btn-sm btn-outline-secondary copy-btn"
                                    onClick={() => copyToClipboard(fullName, 0, 0, 0)}
                                >
                                    <i className="fas fa-copy"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    );
                    
                    loansDataCurrent[item.id] = {
                        items: [
                            { key: 'fullName', value: fullNameWithLink },
                            { key: 'loanProcessingID', value: 'Error' },
                            { key: 'dateGrantedDue', value: 'Error calculating dates' },
                            { key: 'amountGranted', value: <ToPeso amount={item.amountGranted} /> },
                            { key: 'interestAmount', value: <ToPeso amount={((item.amountGranted * (item.interestRate/100)) * item.loanTerm)} /> },
                            { key: 'totalBalance', value: 'Error' },
                            { key: 'interestRate', value: `${item.interestRate}%` },
                            { key: 'term', value: item.loanTerm },
                            { key: 'amountDue', value: 'Error' },
                            { key: 'penalty', value: 'Error' },
                            { key: 'totalAmountDue', value: 'Error' }
                        ]
                    };
                }
            }

            setLoanCurrent(loansDataCurrent);
            setLoanCurrentTotals(response.data.current.totals);

            // Process past due loans
            const loansDataPastdue = {};
            for (const item of response.data.pastDue.loans) {
                const fullName = `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`;

                try {
                    const dates = calculateDueDates(
                        parseISO(item.dateGranted), 
                        item.loanInterval, 
                        item.loanTerm, 
                        item.amountGranted, 
                        item.interestRate, 
                        item.advanceInterest
                    );
                    const paymentsArr = calculatePayments(
                        parseISO(item.dateGranted), 
                        item.loanInterval, 
                        item.loanTerm, 
                        item.amountGranted, 
                        item.interestRate, 
                        item.payments, 
                        item.advanceInterest
                    );
                    const textdates = `${format(parseISO(item.dateGranted), 'MM/dd/yyyy')} - ${dates[dates.length - 1].date}`;

                    const baseAmountDue = calculateAmountDue(dates, item.payments, endDate ? parseISO(endDate) : new Date());
                    // Get penalty from the penalties map
                    const penaltyAmount = getPenaltyAmountFromAPI(allPenalties, item.id);
                    const totalDue = baseAmountDue + penaltyAmount;

                    const fullNameWithLink = (
                        <div className="d-flex align-items-center">
                            <a href={`/applicant/${item.applicant.id}`} className="me-2">
                                {fullName}
                            </a>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Copy payment details</Tooltip>}
                            >
                                <button 
                                    className="btn btn-sm btn-outline-secondary copy-btn"
                                    onClick={() => copyToClipboard(fullName, baseAmountDue, penaltyAmount, totalDue)}
                                >
                                    <i className="fas fa-copy"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    );

                    const loansnum = item.id.toString().padStart(6, '0');
                    const loansNumWithLink = (
                        <a href={`/loans/${item.id}`}>
                            {loansnum}
                        </a>
                    );

                    const loanDetails = {
                        items: [
                            { key: 'fullName', value: fullNameWithLink },
                            { key: 'loanProcessingID', value: loansNumWithLink },
                            { key: 'dateGrantedDue', value: textdates },
                            { key: 'amountGranted', value: <ToPeso amount={item.amountGranted} /> },
                            { key: 'interestAmount', value: <ToPeso amount={((item.amountGranted * (item.interestRate/100)) * item.loanTerm)} /> },
                            { key: 'totalBalance', value: <ToPeso amount={paymentsArr.remainingBalance} /> },
                            { key: 'interestRate', value: `${item.interestRate}%` },
                            { key: 'term', value: item.loanTerm },
                            { key: 'amountDue', value: <ToPeso amount={baseAmountDue} /> },
                            { key: 'penalty', value: <ToPeso amount={penaltyAmount} /> },
                            { key: 'totalAmountDue', value: <ToPeso amount={totalDue} /> }
                        ]
                    };

                    loansDataPastdue[item.id] = loanDetails;
                } catch (error) {
                    console.error(`Error processing loan ${item.id}:`, error);
                    const fullNameWithLink = (
                        <div className="d-flex align-items-center">
                            <a href={`/applicant/${item.applicant.id}`} className="me-2">
                                {fullName}
                            </a>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Copy payment details</Tooltip>}
                            >
                                <button 
                                    className="btn btn-sm btn-outline-secondary copy-btn"
                                    onClick={() => copyToClipboard(fullName, 0, 0, 0)}
                                >
                                    <i className="fas fa-copy"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    );
                    
                    loansDataPastdue[item.id] = {
                        items: [
                            { key: 'fullName', value: fullNameWithLink },
                            { key: 'loanProcessingID', value: 'Error' },
                            { key: 'dateGrantedDue', value: 'Error calculating dates' },
                            { key: 'amountGranted', value: <ToPeso amount={item.amountGranted} /> },
                            { key: 'interestAmount', value: <ToPeso amount={((item.amountGranted * (item.interestRate/100)) * item.loanTerm)} /> },
                            { key: 'totalBalance', value: 'Error' },
                            { key: 'interestRate', value: `${item.interestRate}%` },
                            { key: 'term', value: item.loanTerm },
                            { key: 'amountDue', value: 'Error' },
                            { key: 'penalty', value: 'Error' },
                            { key: 'totalAmountDue', value: 'Error' }
                        ]
                    };
                }
            }

            setLoanPastdue(loansDataPastdue);
            setLoanPastdueTotals(response.data.pastDue.totals);
            
            const paymentHeaders = [
                { value: "Borrower's Name" },
                { value: "Loan No." },
                { value: "Date Granted - Due Date" },
                { value: "Principal" },
                { value: "Interest" },
                { value: "Balance" },
                { value: "Int Rate" },
                { value: "Term" },
                { value: "Amount Due" },
                { value: "Penalty Amount" },
                { value: "Total Amount Due" }
            ];

            setPaymentHeaders(paymentHeaders);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setShowSpinner(false);
        }
    }, []); // Empty dependency array since it doesn't rely on any component state

    useEffect(() => {
        fetchLoans(endDate);
    }, [endDate, fetchLoans]); // Add fetchLoans to the dependency array

    const handleGeneratePDF = async () => {
        setShowSpinner(true);
        try {
            const doc = new jsPDF('landscape');
    
            const centerText = (text, y, fontWeight = 'normal') => {
                doc.setFont(undefined, fontWeight);
                const pageWidth = doc.internal.pageSize.getWidth();
                const textWidth = doc.getTextDimensions(text).w;
                const textX = (pageWidth - textWidth) / 2;
                doc.text(text, textX, y);
            };
    
            const formatDate = (date) => {
                const d = new Date(date);
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const year = d.getFullYear();
                return `${month}/${day}/${year}`;
            };
    
            const logoImg = await fetch(logo);
            const logoBlob = await logoImg.blob();
            const logoURL = URL.createObjectURL(logoBlob);
            
            // Adjust logo dimensions to keep width under 2 points with proper aspect ratio
            const logoWidth = 50; // Just under 2 points
            const aspectRatio = 60 / 12.30; // Calculate original aspect ratio
            const logoHeight = logoWidth / aspectRatio; // Maintain aspect ratio

            doc.addImage(logoURL, 'PNG', 10, 3, logoWidth, logoHeight);
    
            doc.setFontSize(14);
            centerText("Loan Listing By Loan Status", 10, 'bold');
    
            if (endDate) {
                doc.setFontSize(12);
                centerText(`As of ${formatDate(endDate)}`, 19);
            }
    
            const tableColumn = paymentHeaders.map(header => header.value);
    
            let currentY = 30; // Start position for the first table
    
            // Fetch the loan records
            const loansResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/loanstatus`, {
                params: {
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });

            // Fetch all penalties once
            const allPenalties = await fetchAllPenalties(endDate);

            const curloans = loansResponse.data.current.loans;
            const curtotals = loansResponse.data.current.totals;
            const pastloans = loansResponse.data.pastDue.loans;
            const pasttotals = loansResponse.data.pastDue.totals;
            
            if (curloans.length > 0) {
                
                const tableRowsPromises = curloans.map(async (item) => {
                    try {
                        const dateGranted = item.dateGranted ? parseISO(item.dateGranted) : new Date();
                        if (isNaN(dateGranted.getTime())) {
                            throw new Error('Invalid dateGranted');
                        }

                        const dates = calculateDueDates(
                            dateGranted, 
                            item.loanInterval, 
                            item.loanTerm, 
                            item.amountGranted, 
                            item.interestRate, 
                            item.advanceInterest
                        );
                        const paymentsArr = calculatePayments(
                            dateGranted, 
                            item.loanInterval, 
                            item.loanTerm, 
                            item.amountGranted, 
                            item.interestRate, 
                            item.payments, 
                            item.advanceInterest
                        );

                        const lastDueDate = dates[dates.length - 1]?.date;
                        if (!lastDueDate) {
                            throw new Error('Invalid lastDueDate');
                        }

                        const effectiveEndDate = endDate ? parseISO(endDate) : new Date();
                        if (isNaN(effectiveEndDate.getTime())) {
                            throw new Error('Invalid endDate');
                        }
        
                        const amountDue = calculateAmountDue(dates, item.payments, effectiveEndDate);
                        const penaltyAmount = getPenaltyAmountFromAPI(allPenalties, item.id);

                        return [
                            `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                            item.id.toString().padStart(6, '0'),
                            `${format(dateGranted, 'MM/dd/yyyy')} - ${lastDueDate}`,
                            parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(((item.amountGranted * (item.interestRate/100)) * item.loanTerm)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(paymentsArr.remainingBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            `${item.interestRate}%`,
                            item.loanTerm,
                            parseFloat(amountDue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(penaltyAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(amountDue + penaltyAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        ];
                    } catch (error) {
                        console.error(`Error processing loan ${item.id}:`, error);
                        return [
                            `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                            item.id.toString().padStart(6, '0'),
                            'Error calculating dates',
                            parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(((item.amountGranted * (item.interestRate/100)) * item.loanTerm)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            'Error',
                            `${item.interestRate}%`,
                            item.loanTerm,
                            'Error',
                            'Error',
                            'Error'
                        ];
                    }
                });
                
                const tableRows = await Promise.all(tableRowsPromises);

                const currentheader = [
                    'CURRENT','','','','','','','','','',''
                ];

                tableRows.unshift(currentheader);

                const currenttotals = [
                    'Number of Accounts:',
                    curtotals.count,
                    '',
                    parseFloat(curtotals.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(curtotals.interest).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(curtotals.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    '','','','',''
                ];

                tableRows.push(currenttotals);


                doc.autoTable({
                    head: [tableColumn],
                    body: tableRows,
                    startY: currentY,
                    didParseCell: function (data) {
                        const rowIndex = data.row.index;
                        if (rowIndex === 0) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                });

                currentY = doc.lastAutoTable.finalY + 20; // Update currentY to be below the last table
            }


            if (pastloans.length > 0) {
                
                const tableRowsPromises = pastloans.map(async (item) => {
                    try {
                        const dateGranted = item.dateGranted ? parseISO(item.dateGranted) : new Date();
                        if (isNaN(dateGranted.getTime())) {
                            throw new Error('Invalid dateGranted');
                        }

                        const dates = calculateDueDates(
                            dateGranted, 
                            item.loanInterval, 
                            item.loanTerm, 
                            item.amountGranted, 
                            item.interestRate, 
                            item.advanceInterest
                        );
                        const paymentsArr = calculatePayments(
                            dateGranted, 
                            item.loanInterval, 
                            item.loanTerm, 
                            item.amountGranted, 
                            item.interestRate, 
                            item.payments, 
                            item.advanceInterest
                        );

                        const lastDueDate = dates[dates.length - 1]?.date;
                        if (!lastDueDate) {
                            throw new Error('Invalid lastDueDate');
                        }

                        const effectiveEndDate = endDate ? parseISO(endDate) : new Date();
                        if (isNaN(effectiveEndDate.getTime())) {
                            throw new Error('Invalid endDate');
                        }
        
                        const amountDue = calculateAmountDue(dates, item.payments, effectiveEndDate);
                        const penaltyAmount = getPenaltyAmountFromAPI(allPenalties, item.id);

                        return [
                            `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                            item.id.toString().padStart(6, '0'),
                            `${format(dateGranted, 'MM/dd/yyyy')} - ${lastDueDate}`,
                            parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(((item.amountGranted * (item.interestRate/100)) * item.loanTerm)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(paymentsArr.remainingBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            `${item.interestRate}%`,
                            item.loanTerm,
                            parseFloat(amountDue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(penaltyAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(amountDue + penaltyAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        ];
                    } catch (error) {
                        console.error(`Error processing loan ${item.id}:`, error);
                        return [
                            `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                            item.id.toString().padStart(6, '0'),
                            'Error calculating dates',
                            parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            parseFloat(((item.amountGranted * (item.interestRate/100)) * item.loanTerm)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                            'Error',
                            `${item.interestRate}%`,
                            item.loanTerm,
                            'Error',
                            'Error',
                            'Error'
                        ];
                    }
                });
                
                const tableRows = await Promise.all(tableRowsPromises);

                const pastheader = [
                    'PASTDUE','','','','','','','','','',''
                ];

                tableRows.unshift(pastheader);

                const pastduetotals = [
                    'Number of Accounts:',
                    pasttotals.count,
                    '',
                    parseFloat(pasttotals.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(pasttotals.interest).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(pasttotals.balance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    '','','','',''
                ];

                tableRows.push(pastduetotals);

                doc.autoTable({
                    head: [tableColumn],
                    body: tableRows,
                    startY: currentY,
                    didParseCell: function (data) {
                        const rowIndex = data.row.index;
                        if (rowIndex === 0) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                });
            }
    
            const now = new Date();
            const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
            doc.save(`Loan_Listing_By_Loan_Status_${timestamp}.pdf`);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSpinner(false);
        }
    };       

    const handleClick = (e) => {
        e.preventDefault();
        handleGeneratePDF();
    };

    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            
            {/* Toast notification */}
            <ToastContainer 
                position="bottom-end" 
                className="p-3" 
                style={{ zIndex: 1050 }}
            >
                <Toast 
                    onClose={() => setShowToast(false)} 
                    show={showToast} 
                    delay={3000} 
                    autohide
                >
                    <Toast.Header>
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong className="me-auto">Copy Successful</strong>
                        <small>just now</small>
                    </Toast.Header>
                    <Toast.Body>
                        <div className="mb-1 fw-semibold">{toastBorrower}</div>
                        <div className="small text-muted">{toastMessage}</div>
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <div className="filters-container">
                <div className="date-range-controls">
                    <label htmlFor="endDate">As of: </label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <Button href="#" onClick={handleClick} className="generate-pdf-button">
                    <i className="fa-solid fa-print"></i> Export
                </Button>
            </div>
            {loanCurrent && (
                <>
                    <h3 className="table-heading">Current</h3>
                    <Table className="list-container" hover responsive>
                        <thead className="list-title">
                            <tr>
                                {paymentHeaders.map((item, index) => (
                                    <th className="list-header-item" key={index}>{item.value}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.values(loanCurrent).map((item, index) => (
                                <tr className="list-body" key={index}>
                                    {item.items.map((subitem, subindex) => (
                                        <td className="list-body-item" key={`${index}+${subindex}`}>
                                            {subitem.value}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="list-body">
                                <td className="list-body-item">Number of Accounts:</td>
                                <td className="list-body-item">{loanCurrentTotals.count}</td>
                                <td className="list-body-item">&nbsp;</td>
                                <td className="list-body-item"><ToPeso amount={loanCurrentTotals.amountGranted} /></td>
                                <td className="list-body-item"><ToPeso amount={loanCurrentTotals.interest} /></td>
                                <td className="list-body-item"><ToPeso amount={loanCurrentTotals.balance} /></td>
                                <td className="list-body-item" colSpan={6}>&nbsp;</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr className="page-buttons">
                                <td colSpan="12">
                                    &nbsp;
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </>
            )}

            {loanPastdue && (
                <>
                    <h3 className="table-heading">Past Due</h3>
                    <Table className="list-container" hover responsive>
                        <thead className="list-title">
                            <tr>
                                {paymentHeaders.map((item, index) => (
                                    <th className="list-header-item" key={index}>{item.value}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.values(loanPastdue).map((item, index) => (
                                <tr className="list-body" key={index}>
                                    {item.items.map((subitem, subindex) => (
                                        <td className="list-body-item" key={`${index}+${subindex}`}>
                                            {subitem.value}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="list-body">
                                <td className="list-body-item">Number of Accounts:</td>
                                <td className="list-body-item">{loanPastdueTotals.count}</td>
                                <td className="list-body-item">&nbsp;</td>
                                <td className="list-body-item"><ToPeso amount={loanPastdueTotals.amountGranted} /></td>
                                <td className="list-body-item"><ToPeso amount={loanPastdueTotals.interest} /></td>
                                <td className="list-body-item"><ToPeso amount={loanPastdueTotals.balance} /></td>
                                <td className="list-body-item" colSpan={6}>&nbsp;</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr className="page-buttons">
                                <td colSpan="12">
                                    &nbsp;
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </>
            )}

        </>
    );
}

export default ReportLoanListingByLoanStatus;
